/**
 * Wallet filtering hook for Hyperliquid
 * Determines which wallets are eligible and should be monitored
 */

import { useMemo } from 'react';

import { useDataset } from '@infinex/user';

import { useWalletsV2Wallets } from '../../user/hooks';

export interface HyperliquidWalletInfo {
  address: string;
  walletId: string;
  walletName: string;
  isImported?: boolean;
}

export interface UseWalletFilteringReturn {
  eligibleWallets: HyperliquidWalletInfo[];
  monitoredWallets: HyperliquidWalletInfo[];
  monitoredWalletIds: Set<string>;
  monitoredWalletAddresses: Set<string>;
}

/**
 * Hook to filter and determine which wallets should be monitored for Hyperliquid
 *
 * Uses the perps dataset to determine active wallets
 *
 * @returns Filtered wallet lists and lookup sets
 *
 * @example
 * const { eligibleWallets, monitoredWallets } = useWalletFiltering();
 */
export function useWalletFiltering(): UseWalletFilteringReturn {
  const { data: walletsById } = useWalletsV2Wallets();
  const { data: perpsData } = useDataset('perps');

  // Extract unique Hyperliquid wallet addresses from the perps dataset.
  // A wallet is considered active if it has a Hyperliquid perp-margin balance
  // (these are produced 1:1 from the provider's clearing-house state).
  const activeHyperliquidAddresses = useMemo(() => {
    const addresses = new Set<string>();

    for (const balance of perpsData?.balances ?? []) {
      if (
        balance.providerId === 'hyperliquid' &&
        balance.balanceKind === 'perp_margin'
      ) {
        addresses.add(balance.accountId.toLowerCase());
      }
    }

    return addresses;
  }, [perpsData]);

  // Build a map of address -> wallet info for all EVM EOA wallets
  const addressToWalletMap = useMemo(() => {
    if (!walletsById) {
      return new Map<string, HyperliquidWalletInfo>();
    }

    const map = new Map<string, HyperliquidWalletInfo>();

    for (const wallet of Object.values(walletsById)) {
      const evmAddress = Object.values(wallet.chainRuntimeAddresses).find(
        (addr) => addr.chainRuntime === 'evm' && addr.kind === 'EOA'
      );

      if (evmAddress) {
        const addressLower = evmAddress.address.toLowerCase();
        map.set(addressLower, {
          address: evmAddress.address,
          walletId: wallet.id,
          walletName: wallet.label,
          isImported: wallet.isImported,
        });
      }
    }

    return map;
  }, [walletsById]);

  // All eligible wallets (all EVM EOAs) - for metadata and UI
  const eligibleWallets = useMemo(() => {
    return Array.from(addressToWalletMap.values());
  }, [addressToWalletMap]);

  // Monitored wallets: main wallet (always) + wallets with activity from perps dataset
  // TODO: how to deal with more than 10 accounts?
  // https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/rate-limits-and-user-limits
  // "Maximum of 10 unique users across user-specific websocket subscriptions"
  const monitoredWallets = useMemo(() => {
    const monitored: HyperliquidWalletInfo[] = [];

    for (const wallet of eligibleWallets) {
      // Always include main wallet (non-imported)
      if (!wallet.isImported) {
        monitored.push(wallet);
        continue;
      }

      // Include imported wallets only if they have activity in perps dataset
      if (activeHyperliquidAddresses.has(wallet.address.toLowerCase())) {
        monitored.push(wallet);
      }
    }

    return monitored;
  }, [eligibleWallets, activeHyperliquidAddresses]);

  const monitoredWalletIds = useMemo(
    () => new Set(monitoredWallets.map((wallet) => wallet.walletId)),
    [monitoredWallets]
  );

  const monitoredWalletAddresses = useMemo(
    () =>
      new Set(
        monitoredWallets
          .map((wallet) => wallet.address?.toLowerCase())
          .filter(Boolean) as string[]
      ),
    [monitoredWallets]
  );

  return {
    eligibleWallets,
    monitoredWallets,
    monitoredWalletIds,
    monitoredWalletAddresses,
  };
}
